// Copyright (c) 2025 GeoVerses. All rights reserved.

import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';

const SignIn = ({ onGoogleSignIn, onEmailSignIn, onEmailSignUp }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (isSignUp && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    try {
      if (isSignUp) {
        await onEmailSignUp(email, password);
      } else {
        await onEmailSignIn(email, password);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetOpen = () => {
    setResetEmail(email);
    setResetMessage('');
    setResetError('');
    setResetOpen(true);
  };

  const handleResetClose = () => {
    setResetOpen(false);
    setResetEmail('');
    setResetMessage('');
    setResetError('');
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setResetMessage('');
    setResetError('');
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Password reset email sent! Check your inbox.');
    } catch (err) {
      setResetError(err.message);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '360px', alignSelf: 'center' }}>
      <Button variant="contained" onClick={onGoogleSignIn} fullWidth>
        Sign in with Google
      </Button>
      <Divider sx={{ width: '100%', my: 2 }}>or</Divider>
      <form onSubmit={handleEmailSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          fullWidth
          size="small"
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          fullWidth
          size="small"
        />
        {isSignUp && (
          <TextField
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            fullWidth
            size="small"
            error={confirmPassword.length > 0 && password !== confirmPassword}
            helperText={confirmPassword.length > 0 && password !== confirmPassword ? 'Passwords do not match' : ''}
          />
        )}
        {error && (
          <Typography variant="body2" color="error">{error}</Typography>
        )}
        <Button variant="contained" type="submit" fullWidth>
          {isSignUp ? 'Sign Up' : 'Sign In'}
        </Button>
      </form>
      {!isSignUp && (
        <Button
          size="small"
          onClick={handleResetOpen}
          sx={{ mt: 1 }}
        >
          Forgot password?
        </Button>
      )}
      <Button
        size="small"
        onClick={() => { setIsSignUp(!isSignUp); setError(''); setConfirmPassword(''); }}
        sx={{ mt: 1 }}
      >
        {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
      </Button>

      {/* Password Reset Dialog */}
      <Dialog open={resetOpen} onClose={handleResetClose} fullWidth maxWidth="xs">
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>
          <form id="reset-form" onSubmit={handleResetSubmit}>
            <TextField
              label="Email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              required
              fullWidth
              size="small"
            />
          </form>
          {resetMessage && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>{resetMessage}</Typography>
          )}
          {resetError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>{resetError}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetClose}>Cancel</Button>
          <Button variant="contained" type="submit" form="reset-form">Send Reset Email</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SignIn;